import React, { Component } from 'react';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '../Fields/PaperContainer';
import { Grid as GridLayout } from '@material-ui/core';
import Subheader from '@markinson/uicomponents-v2/Subheader';
import Typography from '@markinson/uicomponents-v2/Typography';
import classNames from 'classnames';
import renderComponent from '../RenderComponent';
import cssStyles from './DynamicFormView.scss';
import { OPERATIONS } from 'utils/operations';
import { isNull, isPrecedentFieldsFilled } from 'utils/utils';
import WarningIcon from 'assets/WarningIcon';

const iconMap = {
  Warning: WarningIcon,
};

const styles = theme => ({
  paper: {
    padding: theme.spacing.unit * 2,
    color: theme.palette.text.secondary,
    marginBottom: 16,
    marginRight: 16,
    height: 'calc(100% - 48px)',
  },
});

const SubheaderStyles = {
  fontSize: '18px',
  fontWeight: 100,
  marginBottom: 0,
  paddingLeft: 0
}

const MIN_COLUMN_WIDTH = 400;

const NumericTypes = ['Number', 'Int32', 'Int64', 'Decimal', 'Currency', 'Percentage'];
const ALLOWED_PARSE_FIELDS = ['TEXT_FIELD', 'TEXT_AREA'];

const required = value => (value || typeof value === 'number' ? undefined : 'Required')

const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const validateMultiEmails = (allEmails = '') => {
  const emails = allEmails?.split(';').map(email => email.trim());
  for (let email of emails) {
    if (email && !emailPattern.test(email.toString().toLowerCase())) {
      return 'Invalid Email';
    }
  }
  return undefined;
}

const validateEmail = (email = '') => {
  return email && !emailPattern.test(email.toString().toLowerCase()) ? 'Invalid Email' : undefined
}

const validateConstraints = (data) => {
  const { value, constraints, type } = data;
  if (value && constraints) {
    if (constraints.minLength !== undefined) {
      if (value.length < constraints.minLength) {
        return 'Invalid Length';
      }
    }
    if (constraints.maxLength !== undefined) {
      if (value.length > constraints.maxLength) {
        return 'Invalid Length';
      }
    }
    if (type === 'number' && constraints.minValue !== undefined) {
      if (Number(value) < constraints.minValue) {
        return 'Invalid Value';
      }
    }
    if (type === 'number' && constraints.maxValue !== undefined) {
      if (Number(value) > constraints.maxValue) {
        return 'Invalid Value';
      }
    }
  }
  return undefined;
}

class DynamicFormView extends Component {

  constructor(props) {
    super();
    this.state = {
      readOnly: props.operationMode === OPERATIONS.BROWSE,
    }
  }

  componentWillUnmount() {
    const { resetFormAsyncErrors, formName } = this.props;
    resetFormAsyncErrors(formName);
  }

  renderField = (fieldSchema, rowItemInd) => {
    const { props, type, style } = fieldSchema;
    const {
      valuesSchema, operationMode, onBlur, customerId, entity, supplierId, bin, warehouse, productId, widgetTypeId, dashboardType,
      fieldExtensions, formValues = {}, isV2, isV3, worksaleId, setFieldAsyncError, formName
    } = this.props;

    let isRequired = !!props.required;
    let isReadOnly = this.state.readOnly;

    if (valuesSchema && valuesSchema[props.name]) {
      const objVal = valuesSchema[props.name];
      isRequired = isRequired || (objVal && objVal.Required)
      isReadOnly = isReadOnly || (objVal && objVal.ReadOnly)
    }


    if (fieldExtensions && fieldExtensions[props.name]) {
      const extendedProperties = fieldExtensions[props.name];
      const handleToggleChange = (e) => {
        const extendedChange = extendedProperties['onChange'];
        if (extendedChange) extendedChange(e);
      }
      for (let propertyName in extendedProperties) {
        if (extendedProperties.hasOwnProperty(propertyName)) {
          if (type === 'TOGGLE_FIELD' && propertyName === 'onChange') {
            props['onChange'] = handleToggleChange;
          } else {
            props[propertyName] = extendedProperties[propertyName];
          }
        }
      }
    }

    let validations = isRequired ? [required] : null;
    if (props.constraints) {
      const resp = (value) => {
        const data = { value, constraints: props.constraints, type: props.type }
        return validateConstraints(data)
      }
      validations = validations ? [...validations, resp] : [resp]
    }
    if (props && (type === 'ACTION_FIELD' || type === 'TEXT_FIELD') && props.type === 'email') {
      validations = validations ? [...validations, props?.allowSingleEmail? validateEmail : validateMultiEmails] : [props?.allowSingleEmail ? validateEmail : validateMultiEmails]
    }

    if (type === 'SUBHEADER') {
      return <Subheader
        key={rowItemInd}
        style={SubheaderStyles}>
        {props.value}
      </Subheader>;
    }

    if (type === 'TYPOGRAPHY') {
      const IconComponent = props.icon ? iconMap[props.icon] : null;

      return <Typography
        key={rowItemInd}
        type={props.type}
        customStyles={{
          display: 'flex',
          alignItems: 'center',
          ...props.customStyles
        }}
      >
        {IconComponent && props.text && (
          <IconComponent style={{ color: props.iconColor || 'inherit', marginRight: 8 }} />
        )}
        {props.text}
      </Typography>;
    }

    if ((fieldSchema.type === 'ADDRESS_BLOCK' || fieldSchema.type === 'ADDRESS_BLOCK_EX') && valuesSchema) {
      isReadOnly = isReadOnly || addressReadOnly(props.name);
    }

    function addressReadOnly(type) {
      let keys = Object.keys(valuesSchema).filter(index => index.startsWith(type));
      let readOnly = false;
      keys.forEach(element => {
        const ReadOnly = valuesSchema[element].ReadOnly
        readOnly = isNull(ReadOnly) ? readOnly : ReadOnly;
      });
      return readOnly;
    }

    if (fieldSchema.props.isCustomerSupplierScoped) {
      props.params = props.params || {};
      props.params.CustomerId = customerId || 0;
      props.params.Supplier = supplierId || 0;
    }
    if (fieldSchema.props.isCustomerScoped) {
      props.params = props.params || {};
      props.params.CustomerId = customerId;
    }
    if (fieldSchema.props.isWorksaleScoped) {
      props.params = props.params || {};
      props.params.WorksaleID = worksaleId;
    }
    if (fieldSchema.props.isEntityScoped) {
      props.params = props.params || {};
      props.params.Entity = entity;
    }
    if (fieldSchema.props.isSupplierScoped) {
      props.params = props.params || {};
      props.params.Supplier = supplierId;
    }
    if (fieldSchema.props.isWarehouseSupplierScoped) {
      props.params = props.params || {};
      props.params.Warehouse = warehouse;
      props.params.Supplier = supplierId;
    }

    if (fieldSchema.props.isWarehouseProductScoped) {
      props.params = props.params || {};
      props.params.Warehouse = warehouse;
      props.params.ProductId = productId;
    }

    if(fieldSchema.props.isWarehouseProductBinScoped) {
      props.params = props.params || {};
      props.params.Warehouse = warehouse;
      props.params.ProductId = productId;
      props.params.Bin = bin;
    }

    if (fieldSchema.props.isProductScoped) {
      props.params = props.params || {};
      props.params.ProductId = productId;
    }

    if (fieldSchema.props.isWarehouseScoped) {
      props.params = props.params || {};
      props.params.Warehouse = warehouse;
    }

    if (fieldSchema.props.isWidgetTypeScoped) {
      props.params = props.params || {};
      props.params.WidgetTypeId = widgetTypeId;
    }

    if (fieldSchema.props.isDashboardTypeScoped) {
      props.params = props.params || {};
      props.params.DashboardType = dashboardType;
    }

    if (fieldSchema.type === 'RESTOCKING_CALCULATOR_FIELD') {
      props.calculator = valuesSchema && valuesSchema.RestockingChargeCalculator;
    }

    const { readOnly, name, label, precedentFields, visible = true, hideInV2 = false, hideInV3 = false, hasDynamicLabel = false, bypassInternalDocumentToggle=false, ...restProps } = props;
    let ReadOnly = isNull(isReadOnly) ? readOnly : isReadOnly;
    if (!isNull(precedentFields)) {
      const _isPrecedentFieldsFilled = isPrecedentFieldsFilled(precedentFields, formValues);
      ReadOnly = _isPrecedentFieldsFilled ? bypassInternalDocumentToggle ? readOnly ?? valuesSchema?.[name]?.ReadOnly?.Value : !_isPrecedentFieldsFilled || ReadOnly : true;
    }

    const fieldLabel = (hasDynamicLabel && valuesSchema.hasOwnProperty(`${name}Label`)) ? valuesSchema[`${name}Label`].Value : label;

    const theField = <Field
      key={rowItemInd}
      name={name}
      component={renderComponent[fieldSchema.type]}
      readOnly={ReadOnly}
      label={fieldLabel}
      validate={validations}
      schema={valuesSchema}
      operationMode={operationMode}
      onBlur={onBlur}
      required={isRequired}
      setFieldAsyncError={(f, e) => setFieldAsyncError(formName, f, e)}
      parse={value => {
        if (!ALLOWED_PARSE_FIELDS.includes(fieldSchema?.type)) return value;
        if (value == null || value === '') return value;

        const type = valuesSchema?.[name]?.Type;

        if (NumericTypes.includes(type) && typeof value === 'string') {
          // Case 1: User is still typing with a dot at the end ("1.")
          if (value.endsWith('.')) {
            return value; // preserve as string
          }

          // Case 2: User entered trailing zeros after a decimal ("1.0", "1.00", etc.)
          if (/\.\d*0$/.test(value)) {
            const prev = this.props.formValues?.[name]; // what's already in redux-form
            const numericPrev = Number(prev);
            const numericNow  = Number(value);
            
            if (numericPrev === numericNow) {
              const prevStr = String(prev);
              const valStr  = String(value);

              // exactly one-character difference, and just an append/remove at the end
              const oneCharDiff =
                Math.abs(prevStr.length - valStr.length) === 1 &&
                (valStr.startsWith(prevStr) || prevStr.startsWith(valStr));

              // return new value only if there is exactly 1 diff in representation
              return oneCharDiff ? value : prev;
            }
          }
        }

        // Case 3: normal numeric behavior
        if (NumericTypes.includes(type)) {
          return Number(value);
        }
        return value;
      }}
      format={value => {
        if(!ALLOWED_PARSE_FIELDS.includes(fieldSchema?.type)) return value;
        if(!value) return value;
        const type = valuesSchema?.[name]?.Type;
        return NumericTypes.includes(type) ? String(value) : value;
      }}
      {...restProps}
    />

    if (!visible) return null;
    if (hideInV2 && isV2) return null;
    if (hideInV3 && isV3) return null;

    if (isNull(style)) return (theField)
    else
      return (
        <div style={style} key={rowItemInd}>
          {theField}
        </div>)
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    return {
      readOnly: nextProps.operationMode === OPERATIONS.BROWSE
    };
  }

  renderGroupLayout(groupFieldSchemas) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        ...groupFieldSchemas?.style
      }} className={cssStyles.group}>
        {groupFieldSchemas.childern.map((fieldSchema, childRowId) => (this.renderField(fieldSchema, childRowId)))}
      </div>
    )
  }

  renderRow = (rowItems, rowInd) => {
    const { classes } = this.props;
    const calculateXSValue = (colItem) => {
      return (colItem && colItem.position.colspan) * 4 || 4;
    }

    return (
      rowItems.map((colItems, rowItemInd) => {
        const columnWidth = (colItems[0] && colItems[0].props) ? colItems[0].props.minColumnWidth || MIN_COLUMN_WIDTH : MIN_COLUMN_WIDTH;

        return (
          <GridLayout item xs={calculateXSValue(colItems[0])} style={{ minWidth: columnWidth, ...(this.props.formContainerStyles ?? {}) }} className={cssStyles.gridItem} key={rowItemInd}>
            {colItems.map((rowItem, colItemInd) => {
              if (rowItem.hideInV2 && this?.props?.isV2) return (<></>);
              if (rowItem.hideInV3 && this?.props?.isV3) return (<></>);

              if (rowItem.type === 'PAPER_CONTAINER') {
                return (
                  <Paper
                    className={classes.paper}
                    key={colItemInd}
                    style={rowItem.props.style}
                    square={rowItem.props.square}
                    name={rowItem.props.name}
                    elevation={rowItem.props.elevation}
                  >
                    <label className={classNames(rowItem.props.label ? cssStyles.header : cssStyles.noHeader)}>{rowItem.props.label}</label>
                    {
                      rowItem.children && rowItem.children.map((childFieldSchema, childRowId) => {
                        if (childFieldSchema.hasOwnProperty('group')) {
                          return this.renderGroupLayout(childFieldSchema.group);
                        } else {
                          return this.renderField(childFieldSchema, childRowId);
                        }
                      })
                    }
                  </Paper>
                )
              }
              if (rowItem.type === 'PLACEHOLDER_CONTAINER') {
                return (null)
              }
              return this.renderField(rowItem, rowItemInd)
            })}
          </GridLayout>
        )
      }))
  }

  onSubmit = (values) => {
    if (values) {
      values.preventDefault();
    }
  }

  render() {
    const { layout } = this.props;
    return (
      <div className={cssStyles.root}>

        <form onSubmit={this.onSubmit} className={cssStyles.formDiv}>
          <GridLayout container spacing={0} className={cssStyles.grid}>
            {layout.map((rows) => (
              rows.map((rowItems, rowInd) => {
                return this?.renderRow(rowItems, rowInd)
              })
            ))}
          </GridLayout>
        </form>
      </div>

    );
  }
}

DynamicFormView.propTypes = {
  classes: PropTypes.object.isRequired,
  layout: PropTypes.array.isRequired
};


export default (withStyles(styles, { index: 1 })(DynamicFormView));